import './OfferPageHero.css';

/**
 * Hero focused on ICP landing templates and domain pairing.
 * Includes a compact live form on desktop and keeps the full form below.
 */
function OfferPageHero({
  formData,
  industries,
  formVariant,
  loading,
  onInputChange,
  onSubmit,
}) {
  const scrollToForm = () => {
    const isMobile = window.matchMedia('(max-width: 900px)').matches;
    if (isMobile) {
      document.getElementById('hero-quick-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    document.getElementById('offer-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="offer-page-hero" aria-label="ICP landing templates and domains">
      <div className="offer-page-hero__inner">
        <div className="offer-page-hero__copy">
          <p className="offer-page-hero__eyebrow">
            <span className="offer-page-hero__pill">ICP templates</span>
            Domain + landing flow built for faster launch
          </p>
          <h1 className="offer-page-hero__title">
            Launch with a better domain and an ICP-ready landing page
          </h1>
          <p className="offer-page-hero__lead">
            Get registrar-verified domain options in minutes — then add a conversion-ready landing page
            and a custom promo video, all done for you within 24 hours.
          </p>
          <div className="offer-page-hero__actions">
            <button type="button" className="offer-page-hero__cta" onClick={scrollToForm}>
              <i className="fa-solid fa-rocket" aria-hidden="true" />
              Generate options
            </button>
            <button type="button" className="offer-page-hero__ghost" onClick={scrollToForm}>
              See how it works
            </button>
          </div>
          <ul className="offer-page-hero__bullets">
            <li>Registrar-verified domains, ranked for brand fit</li>
            <li>Conversion-focused landing structure, ready after checkout</li>
            <li>Live preview mirrors what you receive in delivery</li>
          </ul>
          <div className="offer-page-hero__trust">
            <span className="offer-page-hero__trust-item"><i className="fa-solid fa-rotate-left" aria-hidden="true" /> 7-day money-back</span>
            <span className="offer-page-hero__trust-item"><i className="fa-solid fa-shield-halved" aria-hidden="true" /> Secure checkout</span>
            <span className="offer-page-hero__trust-item"><i className="fa-solid fa-circle-check" aria-hidden="true" /> A LeadIdeal company</span>
          </div>
        </div>

        <div className="offer-page-hero__visual">
          <div className="offer-page-hero__browser">
            <div className="offer-page-hero__chrome">
              <span className="offer-page-hero__dot" />
              <span className="offer-page-hero__dot" />
              <span className="offer-page-hero__dot" />
              <span className="offer-page-hero__url">your-brand.com</span>
            </div>
            <div className="offer-page-hero__canvas offer-page-hero__canvas--mockup" aria-hidden="true">
              <div className="offer-page-hero__mocknav">
                <span className="offer-page-hero__mocklogo" />
                <span className="offer-page-hero__mocknav-links">
                  <span /><span /><span />
                </span>
                <span className="offer-page-hero__mockcta" />
              </div>
              <video
                className="offer-page-hero__video"
                autoPlay
                muted
                loop
                playsInline
                poster="/hero-poster.jpg"
              >
                <source src="/hero.mp4" type="video/mp4" />
              </video>
              <div className="offer-page-hero__mockbody">
                <div className="offer-page-hero__row">
                  <div className="offer-page-hero__section offer-page-hero__section--proof">
                    <span className="offer-page-hero__section-label">Proof & outcomes</span>
                  </div>
                  <div className="offer-page-hero__section offer-page-hero__section--offer">
                    <span className="offer-page-hero__section-label">Offer & pricing</span>
                  </div>
                </div>
                <div className="offer-page-hero__section offer-page-hero__section--contact">
                  <span className="offer-page-hero__section-label">FAQ &amp; contact</span>
                </div>
              </div>
            </div>

            <div className="offer-page-hero__canvas offer-page-hero__canvas--form">
              <form id="hero-quick-form" className="offer-page-hero__form" onSubmit={onSubmit}>
                <h3>{formVariant?.title || 'Tell us about your company and ICP'}</h3>

                <div className="offer-page-hero__form-group">
                  <label htmlFor="hero-company">{formVariant?.fields?.company?.label || 'Company Name *'}</label>
                  <input
                    id="hero-company"
                    name="company"
                    type="text"
                    value={formData?.company || ''}
                    onChange={onInputChange}
                    placeholder={formVariant?.fields?.company?.placeholder || 'e.g., Nebula AI'}
                    required={formVariant?.fields?.company?.required !== false}
                  />
                </div>

                <div className="offer-page-hero__form-row">
                  {formVariant?.fields?.industry?.visible && (
                    <div className="offer-page-hero__form-group">
                      <label htmlFor="hero-industry">{formVariant.fields.industry.label}</label>
                      <select
                        id="hero-industry"
                        name="industry"
                        value={formData?.industry || ''}
                        onChange={onInputChange}
                      >
                        <option value="">{formVariant.fields.industry.placeholder}</option>
                        {(industries || []).map((industry) => (
                          <option key={industry} value={industry}>{industry}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formVariant?.fields?.location?.visible && (
                    <div className="offer-page-hero__form-group">
                      <label htmlFor="hero-location">{formVariant.fields.location.label}</label>
                      <input
                        id="hero-location"
                        name="location"
                        type="text"
                        value={formData?.location || ''}
                        onChange={onInputChange}
                        placeholder={formVariant.fields.location.placeholder}
                      />
                    </div>
                  )}
                </div>

                <div className="offer-page-hero__form-row">
                  {formVariant?.fields?.email?.visible && (
                    <div className="offer-page-hero__form-group">
                      <label htmlFor="hero-email">{formVariant.fields.email.label}</label>
                      <input
                        id="hero-email"
                        name="email"
                        type="email"
                        value={formData?.email || ''}
                        onChange={onInputChange}
                        placeholder={formVariant.fields.email.placeholder}
                        required={!!formVariant.fields.email.required}
                      />
                    </div>
                  )}

                  {formVariant?.fields?.website?.visible && (
                    <div className="offer-page-hero__form-group">
                      <label htmlFor="hero-website">{formVariant.fields.website.label}</label>
                      <input
                        id="hero-website"
                        name="website"
                        type="text"
                        value={formData?.website || ''}
                        onChange={onInputChange}
                        placeholder={formVariant.fields.website.placeholder}
                      />
                    </div>
                  )}
                </div>

                <button type="submit" className="offer-page-hero__form-submit" disabled={loading}>
                  {loading ? 'Building options...' : (formVariant?.submitLabel || 'Generate Domain Options')}
                </button>
              </form>
            </div>
          </div>
          <p className="offer-page-hero__caption">Live two-step form below: enter ICP details, get verified domain options.</p>
        </div>
      </div>
    </section>
  );
}

export default OfferPageHero;
